'use client';
import React from 'react';

import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { QuotesManager, QuoteFeedEvent, Security, Quote, QuoteDetail } from '@benzinga/quotes-manager';
import { useActiveWorkspaceSubscriber } from '@benzinga/pro-tools';

export const useQuoteSubscription = (
  symbol: Security,
): { quote: Quote | undefined; details: QuoteDetail | undefined } => {
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);

  const feed = React.useMemo(() => quotesManager.createQuoteFeed(symbol), [quotesManager, symbol]);

  const [toggle, update] = Hooks.useForceUpdateState();

  useActiveWorkspaceSubscriber(feed, (event: QuoteFeedEvent) => {
    switch (event.type) {
      case 'quote:quote':
      case 'quote:quote_details':
        update();
        break;
    }
  });

  return React.useMemo(
    () =>
      toggle
        ? { details: feed.getDetail(), quote: feed.getQuote() }
        : { details: feed.getDetail(), quote: feed.getQuote() },
    [feed, toggle],
  );
};

export const useQuoteSubscriptionCalc = (
  symbol: Security,
  callback: (quote: { quote: Quote | undefined; details: QuoteDetail | undefined }) => void,
) => {
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);

  const feed = React.useMemo(() => {
    const feed = quotesManager.createQuoteFeed(symbol);
    callback({ details: feed.getDetail(), quote: feed.getQuote() });
    return feed;
  }, [callback, quotesManager, symbol]);

  useActiveWorkspaceSubscriber(feed, (event: QuoteFeedEvent) => {
    switch (event.type) {
      case 'quote:quote':
      case 'quote:quote_details':
        callback({ details: feed.getDetail(), quote: feed.getQuote() });
        break;
    }
  });
};
